import { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  BarChart3,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  fetchAttribution,
  type AttributionProduct,
} from '@/services/shopifyAttributionService';

const PAGE_SIZE = 50;

function DeltaBadge({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground text-xs">—</span>;
  const positive = invert ? value < 0 : value > 0;
  const neutral = value === 0;
  if (neutral) return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      <Minus className="h-3 w-3" /> 0
    </span>
  );
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${positive ? 'text-green-600' : 'text-red-500'}`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {invert ? Math.abs(value).toFixed(2) : `${value > 0 ? '+' : ''}${value.toFixed(1)}%`}
    </span>
  );
}

function ProductRow({ row }: { row: AttributionProduct }) {
  const hostname = (() => {
    try { return new URL(row.product_url).pathname.slice(0, 60); } catch { return row.product_url.slice(0, 60); }
  })();

  return (
    <TableRow>
      <TableCell className="max-w-[200px]">
        <a
          href={row.product_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-blue-600 hover:underline truncate"
          title={row.product_url}
        >
          <span className="truncate">{hostname}</span>
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
        <div className="text-xs text-muted-foreground mt-0.5">
          {new Date(row.completed_at).toLocaleDateString()}
        </div>
      </TableCell>
      <TableCell className="text-right text-xs">
        <div>{row.clicks_before_7d.toLocaleString()}</div>
        <div className="text-muted-foreground">→ {row.clicks_after_7d.toLocaleString()}</div>
      </TableCell>
      <TableCell className="text-center">
        <DeltaBadge value={row.clicks_delta_pct} />
      </TableCell>
      <TableCell className="text-right text-xs">
        <div>{row.impressions_before_7d.toLocaleString()}</div>
        <div className="text-muted-foreground">→ {row.impressions_after_7d.toLocaleString()}</div>
      </TableCell>
      <TableCell className="text-right text-xs">
        <div>{row.position_before != null ? row.position_before.toFixed(1) : '—'}</div>
        <div className="text-muted-foreground">→ {row.position_after != null ? row.position_after.toFixed(1) : '—'}</div>
      </TableCell>
      <TableCell className="text-center">
        {/* position improvement = negative delta is good */}
        <DeltaBadge value={row.position_delta} invert />
      </TableCell>
    </TableRow>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
      <BarChart3 className="h-12 w-12 text-muted-foreground opacity-40" />
      <div>
        <p className="font-medium text-sm">No attribution data yet</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-sm">
          No AEO content has been written back to your store yet. Results will appear
          here once your first content writeback completes.
        </p>
      </div>
    </div>
  );
}

export default function ShopifyROIDashboard() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id || (selectedSite as any)?._id;

  const [products, setProducts] = useState<AttributionProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [hasEmptyState, setHasEmptyState] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (currentOffset: number) => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAttribution(siteId, PAGE_SIZE, currentOffset);
      setProducts(data.products);
      setTotal(data.total);
      setHasEmptyState(data.has_empty_state);
    } catch (err: any) {
      setError(err.message || 'Failed to load attribution data');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    setOffset(0);
    load(0);
  }, [siteId, load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  function handlePrev() {
    const next = Math.max(0, offset - PAGE_SIZE);
    setOffset(next);
    load(next);
  }

  function handleNext() {
    const next = offset + PAGE_SIZE;
    setOffset(next);
    load(next);
  }

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Select a site to view attribution data.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">ROI Attribution</h1>
          <p className="text-sm text-muted-foreground mt-1">
            GSC performance 7 days before vs after AEO content was written back to your store.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(offset)}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Per-product attribution
            {!loading && !hasEmptyState && total > 0 && (
              <Badge variant="secondary" className="ml-auto">{total.toLocaleString()} products</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Clicks, impressions and ranking position compared across the 7-day window before and after each writeback.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : hasEmptyState ? (
            <EmptyState />
          ) : products.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[220px]">Product URL / Writeback date</TableHead>
                    <TableHead className="text-right w-[120px]">Clicks (before → after)</TableHead>
                    <TableHead className="text-center w-[80px]">Δ Clicks</TableHead>
                    <TableHead className="text-right w-[140px]">Impressions (before → after)</TableHead>
                    <TableHead className="text-right w-[130px]">Position (before → after)</TableHead>
                    <TableHead className="text-center w-[80px]">Δ Rank</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((row, i) => (
                    <ProductRow key={`${row.product_url}-${row.completed_at}-${i}`} row={row} />
                  ))}
                </TableBody>
              </Table>

              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                  <span>Page {currentPage} of {totalPages}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrev}
                      disabled={offset === 0 || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNext}
                      disabled={currentPage >= totalPages || loading}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
